// Atalho "Pagar agora" da home do DNA Connect: o título em aberto mais urgente, a um toque.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BellRing, CheckCircle2, Copy, Eye, Loader2, Wallet, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { C, StatusBadge } from '@/design-system/dna';
import { useCopyLine, useFamilyFinance } from './useFamilyFinance';
import { fmtBRL, fmtDate, isOpen } from './organization';
import { canCopyLine, canViewBoleto } from './FinanceTitleCard';
import { FinanceDialogs } from './FinanceDialogs';

const DAY = 86400000;
const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - today) / DAY);
};
const dismissKey = (t) => `dna-reminder-${t.view.key}-${new Date().toISOString().slice(0, 10)}`;
const whenText = (n) => (n === 0 ? 'vence hoje' : n === 1 ? 'vence amanhã' : `vence em ${n} dias`);

/** Lembrete gentil: títulos a vencer em até 3 dias (dispensável por dia). */
function DueReminder({ titles, onCopy, copying }) {
  const [hidden, setHidden] = useState(() => new Set());
  const soon = titles.filter((t) => t.status === 'pendente' && (() => { const n = daysUntil(t.vencimento); return n != null && n >= 0 && n <= 3; })())
    .filter((t) => !hidden.has(t.view.key) && !localStorage.getItem(dismissKey(t)));
  if (!soon.length) return null;
  const t = soon[0];
  const n = daysUntil(t.vencimento);
  const dismiss = () => { soon.forEach((x) => localStorage.setItem(dismissKey(x), '1')); setHidden((h) => new Set([...h, ...soon.map((x) => x.view.key)])); };
  return (
    <div className="relative flex items-start gap-3 rounded-[20px] border px-4 py-3.5 dna-rise" role="status"
      style={{ background: '#F7953A12', borderColor: '#F7953A40' }} data-testid="due-reminder">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: '#F7953A22', color: '#B5611A' }}><BellRing className="h-4.5 w-4.5" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-bold" style={{ color: C.ink }} data-testid="due-reminder-title">
          Lembrete: a mensalidade de {t.view.studentName?.split(' ')[0] || 'seu filho'} {whenText(n)}
        </p>
        <p className="mt-0.5 text-[12.5px]" style={{ color: C.muted }} data-testid="due-reminder-text">
          {fmtBRL(t.valor_atual)} · vencimento {fmtDate(t.vencimento)}{soon.length > 1 ? ` · mais ${soon.length - 1} título(s) nos próximos dias` : ''}. Pague com tranquilidade antes do prazo.
        </p>
        {canCopyLine(t) && (
          <button type="button" onClick={() => onCopy(t)} disabled={copying === t.view.key} data-testid="due-reminder-copy"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-bold transition-colors hover:bg-[#FBF9FD]" style={{ color: C.aubergine }}>
            {copying === t.view.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />} Copiar código do boleto
          </button>
        )}
      </div>
      <button type="button" onClick={dismiss} aria-label="Dispensar lembrete" data-testid="due-reminder-dismiss"
        className="rounded-full p-1 transition-colors hover:bg-white/70" style={{ color: C.muted }}><X className="h-4 w-4" /></button>
    </div>
  );
}

const urgency = (a, b) => (b.status === 'vencido') - (a.status === 'vencido') || String(a.vencimento || '9999').localeCompare(String(b.vencimento || '9999'));

export function PayNowCard() {
  const token = localStorage.getItem('familyToken');
  const navigate = useNavigate();
  const { titles, loading } = useFamilyFinance(token);
  const { copying, copyLine } = useCopyLine(token);
  const [modal, setModal] = useState(null);
  const open = useMemo(() => titles.filter(isOpen).sort(urgency), [titles]);
  const t = open[0];
  const goAll = () => navigate('/familia/financeiro');

  return (<>
    <DueReminder titles={titles} onCopy={copyLine} copying={copying} />
    <section className="relative min-w-0 overflow-hidden rounded-[24px] border bg-white p-5" style={{ borderColor: C.line }} data-testid="pay-now-card">
      <span className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-[.12]" style={{ background: t?.status === 'vencido' ? C.orange : C.green }} />
      <div className="relative flex flex-wrap items-center justify-between gap-2">
        <p className="dna-kicker flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Pagar agora</p>
        {open.length > 1 && <button type="button" onClick={goAll} className="text-[12px] font-semibold" style={{ color: C.purple }} data-testid="pay-now-all">+{open.length - 1} em aberto · ver todos</button>}
      </div>
      {loading && !titles.length ? (
        <p className="relative mt-4 flex items-center gap-2 text-sm" style={{ color: C.muted }} data-testid="pay-now-loading"><Loader2 className="h-4 w-4 animate-spin" /> Buscando seus títulos…</p>
      ) : !t ? (
        <p className="relative mt-3 flex items-center gap-2 text-sm font-semibold" style={{ color: C.green }} data-testid="pay-now-empty"><CheckCircle2 className="h-5 w-5" /> Tudo em dia. Nenhum título em aberto.</p>
      ) : (
        <div className="relative mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold" style={{ color: C.aubergine }} data-testid="pay-now-student">{t.view.studentName}{t.view.installment ? ` · Parcela ${t.view.installment}` : ''}</p>
            <p className="mt-1 text-3xl font-extrabold tabular-nums" style={{ color: C.ink, fontFamily: 'Manrope, sans-serif' }} data-testid="pay-now-value">{fmtBRL(t.valor_atual)}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]" style={{ color: C.muted }}>
              <StatusBadge tone={t.status === 'vencido' ? 'danger' : 'warning'} testid="pay-now-status">{t.status === 'vencido' ? 'Vencido' : 'A vencer'}</StatusBadge>
              <span data-testid="pay-now-due">Vencimento {fmtDate(t.vencimento)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canCopyLine(t) && (
              <Button onClick={() => copyLine(t)} disabled={copying === t.view.key} className="h-11 px-5" data-testid="pay-now-copy">
                {copying === t.view.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Copiar código
              </Button>
            )}
            {canViewBoleto(t) && (
              <Button variant="outline" onClick={() => setModal({ type: 'boleto', title: t })} className="h-11 px-5" data-testid="pay-now-boleto"><Eye className="h-4 w-4" /> Ver boleto</Button>
            )}
            {!canCopyLine(t) && !canViewBoleto(t) && (
              <Button variant="outline" onClick={goAll} className="h-11 px-5" data-testid="pay-now-open">
                {t.boleto_status === 'preparando' ? 'Boleto em preparação' : 'Ver no financeiro'} <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
      <FinanceDialogs modal={modal} onClose={() => setModal(null)} token={token} />
    </section>
  </>);
}
